/**
 * The effect library of the music video (spec `2026-10-17-music24-directed-video-design.md` §8):
 * lyric moments, title cards, transitions and shot effects that go over the clips of a cut.
 *
 * This catalogue is the one contract of the effects. The Remotion render, the browser preview of the
 * editor, the first-cut rules of a bank-clip video and the director prompt read it. Each kind has a
 * family, the shortest and the longest time it can last, its parameters with their limits and
 * defaults, and a hint for the director.
 *
 * `parseMusicVideoEffects` is the one check of an effect list. It repairs and never throws: an
 * unknown kind or a bad id goes, every number is clamped, a bad enum gets its default, a text is cut.
 * The file has no heavy imports (no react-intl, no Node modules).
 */
import { MUSIC_VIDEO_TEXT_MAX_LENGTH } from './musicVideoLook';

export const MUSIC_VIDEO_EFFECT_KINDS = [
  'lyricKinetic',
  'lyricHook',
  'titleCard',
  'artistCard',
  'chapterCard',
  'whip',
  'flashCut',
  'filmBurn',
  'glitch',
  'zoomThrough',
  'splitScreen',
  'freezeStroke',
  'rgbSplit',
  'beatShake',
  'colourPulse',
] as const;
export type MusicVideoEffectKind = (typeof MUSIC_VIDEO_EFFECT_KINDS)[number];

/** `lyric`: over a lyric line. `title`: a text card. `transition`: across a clip edge. `shot`: over a shot. */
export type MusicVideoEffectFamily = 'lyric' | 'title' | 'transition' | 'shot';

export type EffectParamSpec =
  | { key: string; type: 'number'; min: number; max: number; default: number }
  | { key: string; type: 'enum'; values: readonly string[]; default: string }
  | { key: string; type: 'text'; maxLength: number; default: string };

export interface MusicVideoEffectSpec {
  kind: MusicVideoEffectKind;
  family: MusicVideoEffectFamily;
  /** The shortest and the longest time of the effect, in seconds. */
  minSec: number;
  maxSec: number;
  params: readonly EffectParamSpec[];
  /** One or two sentences for the director prompt: what the effect looks like and where it goes. */
  directorHint: string;
}

export interface MusicVideoEffect {
  /** Stable in the document: the editor selects and moves an effect by it. */
  id: string;
  kind: MusicVideoEffectKind;
  /** The place on the song, in seconds. */
  startSec: number;
  endSec: number;
  /** One value for each param of the kind (`MUSIC_VIDEO_EFFECTS[kind].params`), no other key. */
  params: Record<string, number | string>;
}

/** The most effects of one cut. */
export const MUSIC_VIDEO_MAX_EFFECTS = 200;

/** The colours of an effect. `accent`: the accent colour of the look of the release. */
export const MUSIC_VIDEO_EFFECT_COLOURS = ['accent', 'white', 'black'] as const;
export type MusicVideoEffectColour = (typeof MUSIC_VIDEO_EFFECT_COLOURS)[number];

/** The default of the `intensity` param of every kind. */
export const MUSIC_VIDEO_EFFECT_DEFAULT_INTENSITY = 0.6;

/** The longest id of an effect, as the ids of the clips and the custom texts. */
const MAX_ID_LENGTH = 64;

const intensity: EffectParamSpec = {
  key: 'intensity',
  type: 'number',
  min: 0,
  max: 1,
  default: MUSIC_VIDEO_EFFECT_DEFAULT_INTENSITY,
};

const colour = (fallback: MusicVideoEffectColour): EffectParamSpec => ({
  key: 'colour',
  type: 'enum',
  values: MUSIC_VIDEO_EFFECT_COLOURS,
  default: fallback,
});

/** The text of a card. An empty text shows the song value (the title, the artist name). */
const cardText: EffectParamSpec = { key: 'text', type: 'text', maxLength: MUSIC_VIDEO_TEXT_MAX_LENGTH, default: '' };

const cardLayout: EffectParamSpec = {
  key: 'layout',
  type: 'enum',
  values: ['center', 'lowerThird', 'corner'],
  default: 'center',
};

export const MUSIC_VIDEO_EFFECTS: Readonly<Record<MusicVideoEffectKind, MusicVideoEffectSpec>> = {
  lyricKinetic: {
    kind: 'lyricKinetic',
    family: 'lyric',
    minSec: 1.5,
    maxSec: 12,
    params: [
      intensity,
      colour('white'),
      { key: 'place', type: 'enum', values: ['center', 'top', 'bottom'], default: 'center' },
    ],
    directorHint:
      'The words of the lyric line that plays pop in one by one on their sung times. Put it exactly over one lyric line (start and end of the line); the text comes from the lyrics. Use it on verses you want to stress, not on every line.',
  },
  lyricHook: {
    kind: 'lyricHook',
    family: 'lyric',
    minSec: 1.5,
    maxSec: 10,
    params: [intensity, colour('accent'), { key: 'scale', type: 'number', min: 0.5, max: 1.5, default: 1 }],
    directorHint:
      'The lyric line that plays fills the frame in huge bold type that hits on the beat. Put it over the hook or the first chorus line, at most two or three times in a song.',
  },
  titleCard: {
    kind: 'titleCard',
    family: 'title',
    minSec: 1.5,
    maxSec: 6,
    params: [intensity, cardText, colour('white'), cardLayout],
    directorHint:
      'A card with the song title (empty text = the song title). Put it once near the start, over a calm wide shot, often in the first 10 seconds.',
  },
  artistCard: {
    kind: 'artistCard',
    family: 'title',
    minSec: 1.5,
    maxSec: 6,
    params: [intensity, cardText, colour('white'), cardLayout],
    directorHint:
      'A card with the artist name (empty text = the artist name). Put it once near the start, after or with the title card, or once at the end.',
  },
  chapterCard: {
    kind: 'chapterCard',
    family: 'title',
    minSec: 1,
    maxSec: 5,
    params: [intensity, cardText, colour('white'), cardLayout],
    directorHint:
      'A short text card that names a story beat (for example "Part II" or "The night after"). Write the text (at most a few words). Put it at the start of a section where the story turns; use 0 to 4 in a song.',
  },
  whip: {
    kind: 'whip',
    family: 'transition',
    minSec: 0.2,
    maxSec: 1,
    params: [intensity, { key: 'direction', type: 'enum', values: ['left', 'right', 'up', 'down'], default: 'left' }],
    directorHint:
      'A fast motion-blurred pan from one shot to the next. Centre it on a clip edge that falls on a beat. Good for energetic songs and fast cuts.',
  },
  flashCut: {
    kind: 'flashCut',
    family: 'transition',
    minSec: 0.1,
    maxSec: 0.6,
    params: [intensity, colour('white')],
    directorHint:
      'A short flash of colour over a clip edge. Centre it on a strong beat: the first beat of a chorus or a drop.',
  },
  filmBurn: {
    kind: 'filmBurn',
    family: 'transition',
    minSec: 0.4,
    maxSec: 2,
    params: [intensity, colour('accent')],
    directorHint:
      'Warm film burn and light leak that wash over a clip edge. Centre it on a section change. Good for warm, nostalgic, analogue looks.',
  },
  glitch: {
    kind: 'glitch',
    family: 'transition',
    minSec: 0.2,
    maxSec: 1.5,
    params: [intensity],
    directorHint:
      'Digital glitch blocks and tears across a clip edge. Centre it on a clip edge on a beat. Good for electronic, dark or tense moments; do not use it in a soft ballad.',
  },
  zoomThrough: {
    kind: 'zoomThrough',
    family: 'transition',
    minSec: 0.3,
    maxSec: 1.2,
    params: [intensity, { key: 'direction', type: 'enum', values: ['in', 'out'], default: 'in' }],
    directorHint:
      'The camera zooms fast into the end of one shot and out of the start of the next. Centre it on a clip edge; good into a chorus or a drop.',
  },
  splitScreen: {
    kind: 'splitScreen',
    family: 'shot',
    minSec: 1,
    maxSec: 8,
    params: [intensity, { key: 'layout', type: 'enum', values: ['halves', 'thirds', 'grid'], default: 'halves' }],
    directorHint:
      'The shot that plays repeats in two, three or four panels that come in on the beats. Put it over one shot of a busy section, such as a chorus.',
  },
  freezeStroke: {
    kind: 'freezeStroke',
    family: 'shot',
    minSec: 0.5,
    maxSec: 3,
    params: [intensity, colour('white')],
    directorHint:
      'The frame freezes and the person or object gets a drawn outline. Put it on a strong beat or the last word of a line, at the moment a person is clearly in the frame.',
  },
  rgbSplit: {
    kind: 'rgbSplit',
    family: 'shot',
    minSec: 0.3,
    maxSec: 8,
    params: [intensity],
    directorHint:
      'The red, green and blue channels move apart on each beat. Put it over a shot of an intense part (a drop, a bridge build-up).',
  },
  beatShake: {
    kind: 'beatShake',
    family: 'shot',
    minSec: 1,
    maxSec: 16,
    params: [intensity],
    directorHint:
      'The frame shakes on every beat. Put it over a loud, high-energy part such as a chorus or a drop. Keep the intensity low on calm songs.',
  },
  colourPulse: {
    kind: 'colourPulse',
    family: 'shot',
    minSec: 1,
    maxSec: 16,
    params: [intensity, colour('accent')],
    directorHint:
      'A colour tint pulses on every beat and fades. Put it over a chorus or a high-energy part. Subtle at low intensity.',
  },
};

export function isMusicVideoEffectKind(value: unknown): value is MusicVideoEffectKind {
  return typeof value === 'string' && (MUSIC_VIDEO_EFFECT_KINDS as readonly string[]).includes(value);
}

// ---------------------------------------------------------------------------------------------
// Parse
// ---------------------------------------------------------------------------------------------

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

/** Kept to the millisecond, as the times of `musicVideoDoc.ts`. */
function round3(value: number): number {
  return Math.round(value * 1000) / 1000;
}

function clamp(value: number, min: number, max: number): number {
  return Math.min(max, Math.max(min, value));
}

function finiteOr(value: unknown, fallback: number): number {
  return typeof value === 'number' && Number.isFinite(value) ? value : fallback;
}

function idOf(value: unknown): string | null {
  return typeof value === 'string' && value !== '' && value.length <= MAX_ID_LENGTH ? value : null;
}

/** The value of one param: a clamped number, an allowed enum value, a cut text, else the default. */
function paramValue(spec: EffectParamSpec, raw: unknown): number | string {
  switch (spec.type) {
    case 'number':
      return round3(clamp(finiteOr(raw, spec.default), spec.min, spec.max));
    case 'enum':
      return typeof raw === 'string' && spec.values.includes(raw) ? raw : spec.default;
    case 'text':
      return typeof raw === 'string' ? raw.trim().slice(0, spec.maxLength) : spec.default;
    default: {
      const exhaustive: never = spec;
      throw new Error(`unknown param type ${String(exhaustive)}`);
    }
  }
}

/** The params of a kind: every key of the spec, no other key. */
export function effectParamsOf(kind: MusicVideoEffectKind, raw: unknown): Record<string, number | string> {
  const input: Record<string, unknown> = isRecord(raw) ? raw : {};
  const params: Record<string, number | string> = {};
  for (const spec of MUSIC_VIDEO_EFFECTS[kind].params) {
    params[spec.key] = paramValue(spec, input[spec.key]);
  }
  return params;
}

/**
 * The times of an effect, repaired: the start inside the song, the length clamped to
 * `minSec..maxSec` of the kind, and the end inside the song (the start moves back when the end
 * would pass it). Null when the song is shorter than the shortest effect of the kind.
 */
function effectTimes(
  spec: MusicVideoEffectSpec,
  rawStart: unknown,
  rawEnd: unknown,
  durationSec: number
): { startSec: number; endSec: number } | null {
  if (durationSec < spec.minSec) {
    return null;
  }
  let startSec = clamp(finiteOr(rawStart, 0), 0, durationSec);
  const endSec = clamp(finiteOr(rawEnd, startSec + spec.minSec), 0, durationSec);
  const lengthSec = clamp(endSec - startSec, spec.minSec, spec.maxSec);
  if (startSec + lengthSec > durationSec) {
    startSec = durationSec - lengthSec;
  }
  return { startSec: round3(startSec), endSec: round3(Math.min(durationSec, startSec + lengthSec)) };
}

/**
 * Checks the effect list of a doc against a song of `durationSec` seconds. Not a list: an empty list
 * (a stored v1 doc has no `effects` key). An effect with an unknown kind, a bad id or an id that is
 * already in the list goes. The times and the params are repaired (`effectTimes`, `effectParamsOf`).
 * At most `MUSIC_VIDEO_MAX_EFFECTS` effects stay, the first ones of the input. The result is sorted
 * by start; equal starts keep the order of the input. Never throws.
 */
export function parseMusicVideoEffects(input: unknown, durationSec: number): MusicVideoEffect[] {
  if (!Array.isArray(input) || !Number.isFinite(durationSec) || durationSec <= 0) {
    return [];
  }
  const effects: MusicVideoEffect[] = [];
  const ids = new Set<string>();
  for (const raw of input) {
    if (effects.length >= MUSIC_VIDEO_MAX_EFFECTS) {
      break;
    }
    if (!isRecord(raw) || !isMusicVideoEffectKind(raw.kind)) {
      continue;
    }
    const id = idOf(raw.id);
    if (id === null || ids.has(id)) {
      continue;
    }
    const times = effectTimes(MUSIC_VIDEO_EFFECTS[raw.kind], raw.startSec, raw.endSec, durationSec);
    if (times === null) {
      continue;
    }
    ids.add(id);
    effects.push({ id, kind: raw.kind, ...times, params: effectParamsOf(raw.kind, raw.params) });
  }
  return effects
    .map((effect, index) => ({ effect, index }))
    .sort((a, b) => {
      const byStart = a.effect.startSec - b.effect.startSec;
      return byStart !== 0 ? byStart : a.index - b.index;
    })
    .map(({ effect }) => effect);
}

// ---------------------------------------------------------------------------------------------
// The director prompt
// ---------------------------------------------------------------------------------------------

function describeParam(spec: EffectParamSpec): string {
  switch (spec.type) {
    case 'number':
      return `${spec.key}=${spec.min}..${spec.max} (default ${spec.default})`;
    case 'enum':
      return `${spec.key}=${spec.values.join('|')} (default ${spec.default})`;
    case 'text':
      return `${spec.key}=text up to ${spec.maxLength} chars (default "${spec.default}")`;
    default: {
      const exhaustive: never = spec;
      throw new Error(`unknown param type ${String(exhaustive)}`);
    }
  }
}

/**
 * The catalogue as text for the director prompt: one line for each kind with its family, its length
 * range in seconds, its params and its hint. Transitions are centred on a clip edge; the other
 * families lie over the shot or the lyric line that they name.
 */
export function effectCatalogueForPrompt(): string {
  return MUSIC_VIDEO_EFFECT_KINDS.map((kind) => {
    const spec = MUSIC_VIDEO_EFFECTS[kind];
    const params = spec.params.map(describeParam).join('; ');
    return `- ${kind} [${spec.family}, ${spec.minSec}-${spec.maxSec}s] params: ${params}. ${spec.directorHint}`;
  }).join('\n');
}
